import { Injectable } from "@angular/core";
import { CHZoneData } from "@app/area/model/ch-zone";
import { ProjectSelectors } from "@app/project/store";
import { DataActions } from "@app/project/store/actions";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { checkCurrentID } from "@app/shared/resources/data-actions";
import { rekeyDbResultArray } from "@app/shared/resources/resource-objectifier";
import { EquatorZonesService } from "@app/shared/services/equator-api/equator-zones";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { of } from "rxjs";
import { catchError, exhaustMap, map, switchMap } from "rxjs/operators";
import { ZoneActions } from "../actions";

@Injectable()
export class ZoneEffects {
    constructor(
        private readonly deleteConfirmationService: DeleteConfirmationService,
        private readonly actions$: Actions,
        private readonly zoneService: EquatorZonesService,
        private readonly store: Store
    ) {}

    private readonly mapToAction = (src$, loadSuccess, loadFailure) => DataActions.toAction2()(src$, loadSuccess, loadFailure);
    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ZoneActions.load),
            switchMap(({ uuid }) =>
                this.mapToAction(
                    this.zoneService.get(uuid).pipe(
                        checkCurrentID(this.store.pipe(select(ProjectSelectors.getUuid)), uuid),
                        map(rekeyDbResultArray(this.zoneService.dbHelper)),
                        map(zones => ({ uuid, zones }))
                    ),
                    ZoneActions.loadSuccess,
                    ZoneActions.loadFailure
                )
            )
        )
    );

    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ZoneActions.create),
            exhaustMap(({ uuid, zone }) =>
                this.mapToAction(
                    this.zoneService.add(uuid, this.zoneService.dbHelper.toDbObject(zone)).pipe(
                        checkCurrentID<CHZoneData>(this.store.pipe(select(ProjectSelectors.getUuid)), uuid),
                        map(_zone => ({
                            uuid,
                            zone: this.zoneService.dbHelper.fromDbObject(_zone),
                        })),
                        catchError(e => of(e))
                    ),
                    ZoneActions.createSuccess,
                    ZoneActions.createFailure
                )
            ),
            catchError(DataActions.toError2(ZoneActions.createFailure))
        )
    );

    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ZoneActions.update),
            exhaustMap(({ uuid, zone }) =>
                this.mapToAction(
                    this.zoneService.edit(uuid, zone.id, zone.name).pipe(
                        checkCurrentID(this.store.pipe(select(ProjectSelectors.getUuid)), uuid),
                        map(_ => ({ uuid, zone })),
                        catchError(e => of(e))
                    ),
                    ZoneActions.updateSuccess,
                    ZoneActions.updateFailure
                )
            ),
            catchError(DataActions.toError2(ZoneActions.updateFailure))
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ZoneActions.deleteItem),
            exhaustMap(({ uuid, zone }) =>
                this.mapToAction(
                    this.zoneService.remove(uuid, zone.id).pipe(
                        checkCurrentID(this.store.pipe(select(ProjectSelectors.getUuid)), uuid),
                        map(_ => ({ uuid, zone })),
                        catchError(e => of(e))
                    ),
                    ZoneActions.deleteSuccess,
                    ZoneActions.deleteFailure
                )
            ),
            catchError(DataActions.toError2(ZoneActions.deleteFailure))
        )
    );

    deleteConfirmation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ZoneActions.deleteConfirmation),
            exhaustMap(payload =>
                this.deleteConfirmationService.open$(
                    payload,
                    ZoneActions.deleteConfirmationDismiss,
                    ZoneActions.deleteItem,
                    payload.zone.name
                )
            )
        )
    );
}
